import { useState, useEffect, useCallback, useRef } from 'react';

interface UseAutoConfirmOptions {
  onAutoConfirm: () => void;
  delayMs?: number;
  enabled?: boolean;
}

export function useAutoConfirm({
  onAutoConfirm,
  delayMs = 15000,
  enabled = true,
}: UseAutoConfirmOptions) {
  const [countdown, setCountdown] = useState(Math.floor(delayMs / 1000));
  const timerRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const hasAutoConfirmedRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    hasAutoConfirmedRef.current = false;
    setCountdown(Math.floor(delayMs / 1000));

    timerRef.current = setTimeout(() => {
      hasAutoConfirmedRef.current = true;
      onAutoConfirm();
    }, delayMs);

    intervalRef.current = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);

    return clearTimers;
  }, [enabled, delayMs]);

  const cancelAutoConfirm = useCallback(() => {
    if (!hasAutoConfirmedRef.current) {
      clearTimers();
    }
  }, [clearTimers]);

  return { countdown, cancelAutoConfirm };
}
